import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import SectionHeader from '../common/SectionHeader';
import StatusBadge from '../common/StatusBadge';
import { useListTestimonialsQuery } from '../../store/testimonialsApi';

const TestiCard = ({ t }) => (
  <Box
    sx={{
      bgcolor: '#fff',
      border: '1px solid var(--border-l)',
      borderRadius: '16px',
      p: 2.5,
      minWidth: 280,
      maxWidth: 280,
      flexShrink: 0,
      transition: 'all 0.3s',
      '&:hover': { boxShadow: '0 12px 32px rgba(21,42,30,0.1)', transform: 'translateY(-3px)' },
    }}
  >
    <Typography sx={{ color: 'var(--amber)', fontSize: 13, letterSpacing: '2px', mb: 1.5 }}>
      {'★'.repeat(Math.max(1, Math.min(5, t.rating ?? 5)))}
    </Typography>
    <Typography variant="body1" sx={{ fontWeight: 300, mb: 2, fontSize: 13, lineHeight: 1.7 }}>
      "{t.text}"
    </Typography>
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          bgcolor: t.color || 'var(--sage)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: 13,
          flexShrink: 0,
        }}
      >
        {t.avatar || (t.name || '').slice(0, 2).toUpperCase()}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="h5" sx={{ fontSize: 13, lineHeight: 1.3 }}>
          {t.name}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: 11, fontWeight: 300 }}>
          {t.meta}
        </Typography>
      </Box>
      <StatusBadge tone="sage">✓</StatusBadge>
    </Stack>
  </Box>
);

const Testimonials = () => {
  const { data: testimonials = [], isLoading } = useListTestimonialsQuery();
  // Duplicate the list so the marquee scroll loops seamlessly
  const loop = testimonials.length > 0 ? [...testimonials, ...testimonials] : [];

  return (
    <Box sx={{ bgcolor: 'var(--cream)', py: { xs: 8, md: 13 }, overflow: 'hidden' }}>
      <Container maxWidth="xl" sx={{ maxWidth: 1300, px: { xs: 2, md: 7 }, mb: 5.5 }}>
        <SectionHeader
          eyebrow="Patient Stories"
          title="1 million patients trust us 🧡"
          action={
            <Stack direction="row" spacing={0.75}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'var(--sage)' }} />
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'var(--sage-p)' }} />
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'var(--sage-p)' }} />
            </Stack>
          }
        />
      </Container>

      {isLoading ? (
        <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={22} sx={{ color: 'var(--sage)' }} />
        </Box>
      ) : loop.length === 0 ? null : (
        <Box sx={{ overflow: 'hidden', px: { xs: 2, md: 7 } }}>
          <Box
            sx={{
              display: 'flex',
              gap: 1.75,
              width: 'max-content',
              animation: 'marqueeScroll 36s linear infinite',
              '&:hover': { animationPlayState: 'paused' },
              '@keyframes marqueeScroll': {
                '0%': { transform: 'translateX(0)' },
                '100%': { transform: 'translateX(-50%)' },
              },
            }}
          >
            {loop.map((t, i) => (
              <TestiCard key={`${t._id || t.name}-${i}`} t={t} />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Testimonials;
